import { useMemo } from "react";

import { API_URLS } from "shared/constants";
import useHttpActions from "shared/services/api/core/useHttpActions";
import type { FriendlyMatchData, MatchAttendanceModel, MatchModel, MatchTeamsModel } from "shared/types";
import type { CalendarEventModel, ManageCalendarEventsParams } from "shared/types/MatchStatusEnum";

import BaseConverter from "./converters/baseConverter";

import type { TeamInMatchRole } from "../types/CompetitionTeamsStandingModel";
import type { PlayerMatchStatsModel } from "../types/GroupScheduleTypes";
import { playerMatchStatsResolver } from "../types/GroupScheduleTypes";

const useMatchApiService = () => {
	const actions = useHttpActions();

	return useMemo(
		() => ({
			getMatchOverview: async (id: string) => {
				return BaseConverter.convert<MatchModel>(
					await actions.get(API_URLS.MATCH.OVERVIEW, {
						"match-id": id
					})
				);
			},
			getMatchInfo: async (id: string) => {
				return BaseConverter.convert<{ match: MatchModel }>(
					await actions.get(API_URLS.MATCH.INFO, {
						"match-id": id
					})
				);
			},
			getAttendanceOverview: async (entityId: string) => {
				return BaseConverter.convert<MatchAttendanceModel>(
					await actions.get(API_URLS.MATCH.GET_ATTENDANCE, {
						entityId,
						entityType: "MATCH"
					})
				);
			},
			getMatchTeams: async (id: string) => {
				return BaseConverter.convert<{ match: MatchTeamsModel }>(
					await actions.get(API_URLS.MATCH.TEAMS, {
						"match-id": id
					})
				);
			},
			createFriendlyMatch: async (data: FriendlyMatchData) => {
				return BaseConverter.convert<{ match: MatchTeamsModel }>(
					await actions.post(API_URLS.MATCH.CREATE_FRIENDLY, data)
				);
			},
			createCalendarEvent: async (props: CalendarEventModel) => {
				return BaseConverter.convert<{ match: MatchTeamsModel }>(
					await actions.post(API_URLS.MATCH.CALENDAR_EVENT, props)
				);
			},
			manageCalendarEvents: async (params: ManageCalendarEventsParams) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.MATCH.MANAGE_CALENDAR_EVENTS, params)
				);
			},
			downloadMatchReports: async (params: { competitionId?: string; groupId?: string; matchId?: string }) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.get(API_URLS.SOCCER.DOWNLOAD_MATCH_REPORTS, params)
				);
			},
			updateMatchPlayersSquad: async (params: {
				matchId: string;
				squadPlayers: {
					add?: {
						playerId: string;
						team: TeamInMatchRole;
						jerseyNumber?: null | number;
					}[];
					remove?: {
						playerId: string;
						team: TeamInMatchRole;
					}[];
				};
			}) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.MATCH.UPDATE_PLAYERS_SQUAD, params)
				);
			},
			getMatchPlayerStats: async (matchId: string) => {
				return BaseConverter.convert<{ match: PlayerMatchStatsModel }>(
					await actions.get(
						API_URLS.MATCH.PLAYER_STATS,
						{ matchId },
						{
							resolvers: [
								{
									resolver: playerMatchStatsResolver,
									fields: ["match"]
								}
							]
						}
					)
				);
			}
		}),
		[actions]
	);
};

export default useMatchApiService;
